import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useReview } from '../../context/ReviewContext';
import ReviewCard from './ReviewCard';
import { reviewModel, reviewUtils } from '../../data/reviewModel';

const StoreReviewList = ({ 
  storeId, 
  showStats = true, 
  showResponse = true,
  limit = null 
}) => {
  const { t } = useTranslation();
  const { loadStoreReviews, getStoreReviewStats } = useReview();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, 5, 4, 3, 2, 1
  const [currentLimit, setCurrentLimit] = useState(limit);

  useEffect(() => {
    if (storeId) {
      loadReviews();
    }
  }, [storeId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const reviewsData = await loadStoreReviews(storeId);
      setReviews(reviewsData);
      
      if (showStats) {
        const statsData = await getStoreReviewStats(storeId);
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error loading store reviews:', error);
      setError('เกิดข้อผิดพลาดในการโหลดรีวิวร้านค้า');
    } finally {
      setLoading(false);
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === 'all') return true;
    return review.rating === parseInt(filter);
  });

  const displayedReviews = currentLimit ? filteredReviews.slice(0, currentLimit) : filteredReviews;

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p className="text-gray-600">กำลังโหลดรีวิว...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 text-lg mb-2">⚠️</div>
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadReviews}
          className="mt-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
        >
          ลองใหม่
        </button>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 text-4xl mb-4">⭐</div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">ยังไม่มีรีวิว</h3>
        <p className="text-gray-500">เป็นคนแรกที่รีวิวร้านค้านี้</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      {showStats && stats && (
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">สถิติรีวิวร้านค้า</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Average Rating */}
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-500 mb-1">
                {stats.averageRating.toFixed(1)}
              </div>
              <div className="text-sm text-gray-600 mb-2">
                ★ คะแนนเฉลี่ย
              </div>
              <div className="text-xs text-gray-500">
                จาก {stats.totalReviews} รีวิว
              </div>
            </div>

            {/* Rating Distribution */}
            <div className="md:col-span-2">
              <div className="text-sm font-medium text-gray-700 mb-3">การกระจายคะแนน</div>
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((rating) => (
                  <div key={rating} className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600 w-4">{rating}</span>
                    <span className="text-sm text-yellow-500">★</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-yellow-500 h-2 rounded-full"
                        style={{
                          width: `${stats.totalReviews > 0 ? (stats.ratingDistribution[rating] / stats.totalReviews) * 100 : 0}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500 w-8">
                      {stats.ratingDistribution[rating]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Common Tags */}
          {stats.commonTags && stats.commonTags.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-medium text-gray-700 mb-3">แท็กที่พบบ่อย</div>
              <div className="flex flex-wrap gap-2">
                {stats.commonTags.map(({ tag, count }) => {
                  const translationKey = `reviewTags.${tag}`;
                  const translated = t(translationKey, { defaultValue: tag });
                  return (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {translated} ({count})
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center space-x-2">
        <span className="text-sm font-medium text-gray-700">กรองตามคะแนน:</span>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="all">ทั้งหมด</option>
          <option value="5">5 ดาว</option>
          <option value="4">4 ดาว</option>
          <option value="3">3 ดาว</option>
          <option value="2">2 ดาว</option>
          <option value="1">1 ดาว</option>
        </select>
        <span className="text-sm text-gray-500">
          แสดง {displayedReviews.length} จาก {filteredReviews.length} รีวิว
        </span>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {displayedReviews.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            showResponse={showResponse}
            canRespond={false}
          />
        ))}
      </div>

      {/* Load More */}
      {currentLimit && filteredReviews.length > currentLimit && (
        <div className="text-center">
          <button
            onClick={() => setCurrentLimit(null)}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            แสดงรีวิวทั้งหมด ({filteredReviews.length})
          </button>
        </div>
      )}
    </div>
  );
};

export default StoreReviewList;













